import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import {
	CARD_IN_USE_ON_UPDATE_MESSAGE,
	CREDENTIAL_ERROR_CODE,
	CREDENTIAL_ERROR_MESSAGE
} from 'config/credentialErrorCodes';
import { CREDENTIAL_ORIGIN } from 'config/credentialOrigin';
import { CREDENTIAL_TYPE_PASSCODE, CREDENTIAL_TYPE_PASSCODE_CARD } from 'config/credentialTypes';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import { canHoldLogin } from 'config/userType';
import { personPhone } from 'library/helpers/personPhone';

dayjs.extend(isBetween);

export const getCredentialStatus = (credential) => {
	const fallback = { text: 'Unknown', color: 'default' };

	if (!credential) return { text: 'Active', color: 'green' };

	try {
		const { startDate, endDate } = credential;

		// A disabled holder's credentials carry endDate = DisabledAt and may have no startDate.
		if (endDate && dayjs(endDate).year() !== 1 && dayjs().isAfter(dayjs(endDate)))
			return { text: 'Expired', color: 'red' };

		// No dates means permanent.
		if (!startDate || !endDate) return { text: 'Active', color: 'green' };

		const start = dayjs(startDate);
		const end = dayjs(endDate);

		// 🔥 Inner Range Lifesaver: If it's year 1 (0001), it's Permanent = Active
		if (start.year() === 1 && end.year() === 1) return { text: 'Active', color: 'green' };

		const now = dayjs();

		// 1. Is it currently within the time period? -> Active
		if (now.isBetween(start, end, null, '[]')) return { text: 'Active', color: 'green' };

		// 2. Has the current time passed the end date? -> Expired
		if (now.isAfter(end)) return { text: 'Expired', color: 'red' };

		// 3. If it's not active and not expired... it means the start date is in the future.
		return { text: 'Inactive', color: 'orange' }; // Orange reads as pending
	} catch {
		return fallback;
	}
};

// personID → the credentials they hold. A filter per person is the store size times the
// person count, and every per-person credential list wants this shape.
export const groupCredentialsByPersonID = (credentials = []) => {
	const m = new Map();
	credentials.forEach((c) => {
		if (!c.personID) return;
		if (!m.has(c.personID)) m.set(c.personID, []);
		m.get(c.personID).push(c);
	});
	return m;
};

// Why this person cannot hold a mobile credential, or null when they can. It mints their login and
// signs in with their email, so a no-login type and a missing address are both refused server-side.
export const mobileCredentialBlocker = (person) => {
	if (!canHoldLogin(person?.userTypeID)) return 'noLoginType';
	if (!person?.email?.trim()) return 'noEmail';
	return null;
};

// Why a visitor can't get a Cloud Key, or null when they can: it needs somewhere to send the
// link, an end to its lifetime, and at least one door it can open remotely.
export const cloudKeyBlocker = ({ hasContact, validUntil, doorIDs = [] }) => {
	if (!hasContact) return 'noContact';
	if (!validUntil) return 'noPeriod';
	if (!doorIDs.length) return 'noDoors';
	return null;
};

// Where a Cloud Key link goes, clamped to the contact details the recipient actually has.
// Falls back to email when neither is filled — the issuing form's own rule refuses that.
export const resolveCloudKeyChannel = ({ channel, hasEmail, hasPhone }) => {
	if (channel === 'SMS') return hasPhone ? 'SMS' : hasEmail ? 'EMAIL' : 'SMS';
	return hasEmail ? 'EMAIL' : hasPhone ? 'SMS' : 'EMAIL';
};

// The doors a Cloud Key is minted against: every remote-unlockable device the permissions reach,
// directly or through a group. The key SNAPSHOTS them — it does not follow the group afterwards.
export const cloudKeyDoorIDs = (permissions = [], groups = [], unlockDevices = []) => {
	const unlockable = new Set(unlockDevices.map((d) => d.deviceID));
	const reached = new Set();

	for (const row of permissions) {
		if (row.resourceType === 'device') {
			reached.add(row.resourceID);
			continue;
		}
		const group = groups.find((g) => (g.assetGroupID || g.groupID) === row.resourceID);
		(group?.devices || []).forEach((deviceID) => reached.add(deviceID));
	}

	return [...reached].filter((deviceID) => unlockable.has(deviceID));
};

// AU mobiles arrive as 04xxxxxxxx or +614xxxxxxxx — compare the last 9 digits.
const phoneKey = (s) => {
	const d = String(s || '').replace(/\D/g, '');
	return d.length >= 9 ? d.slice(-9) : d;
};

// Is this Cloud Key the person's? Exact by recipientPersonID, else email / AU-mobile for legacy rows.
export const cloudKeyBelongsTo = (key, person) => {
	if (!key || !person) return false;
	if (key.recipientPersonID != null) return key.recipientPersonID === person.personID;
	const email = (person.email || '').trim().toLowerCase();
	if (email && (key.recipientEmail || '').trim().toLowerCase() === email) return true;
	const pk = phoneKey(personPhone(person));
	return !!pk && phoneKey(key.recipientPhone) === pk;
};

// personID → that person's Cloud Keys, for a list of people.
export const groupCloudKeysByPerson = (cloudKeys = [], people = []) => {
	const m = new Map();
	people.forEach((p) =>
		m.set(
			p.personID,
			cloudKeys.filter((k) => cloudKeyBelongsTo(k, p))
		)
	);
	return m;
};

const randomPasscode = (length) =>
	String(Math.floor(Math.random() * 9) + 1) +
	Array.from({ length: length - 1 }, () => Math.floor(Math.random() * 10)).join('');

// A random passcode not in `taken`. Short codes have a small space, so give up after a few tries
// and let the form's uniqueness rule say so rather than spin.
export const generatePasscode = (length, taken) => {
	let code = randomPasscode(length);
	for (let attempt = 0; taken?.has(code) && attempt < 50; attempt += 1) code = randomPasscode(length);
	return code;
};

// A held passcode never moves to another person (the server refuses with PASSCODE_IN_USE).
export const isHeldPasscode = (credential) =>
	credential?.credentialTypeID === CREDENTIAL_TYPE_PASSCODE && Boolean(credential.personID);

// Written to the lock by the app over Bluetooth: the Dashboard can't take it off the lock.
export const isLockFirstCredential = (credential) => credential?.origin === CREDENTIAL_ORIGIN.BLUETOOTH;

// The one-line message for a credential refusal code, or null when none of the codes has one.
export const credentialRefusalMessage = (codes = []) =>
	[...new Set((codes || []).map((code) => CREDENTIAL_ERROR_MESSAGE[code]).filter(Boolean))].join(' ') || null;

// A refused credential write's message. On CARD_IN_USE it names the clashing field that was sent.
export const credentialWriteRefusalMessage = (code, data = {}) => {
	if (code === CREDENTIAL_ERROR_CODE.CARD_IN_USE) {
		const field = Object.keys(CARD_IN_USE_ON_UPDATE_MESSAGE).find((key) => key in data);
		if (field) return CARD_IN_USE_ON_UPDATE_MESSAGE[field];
	}
	return credentialRefusalMessage([code]);
};

// Only IR and Inception templates can be changed, and only within their own integration.
const TEMPLATE_INTEGRATION_IDS = [
	INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id,
	INTEGRATIONS_TYPES.INTEGRATION_INCEPTION.id
];

// Inception templates have no real site code (always 0).
export const cardTemplateHasSiteCode = (t) => t?.integrationID !== INTEGRATIONS_TYPES.INTEGRATION_INCEPTION.id;

// A card template as "Name (site code)" for pickers; name only when it has no site code.
export const cardTemplateLabel = (t) => {
	const siteCode = cardTemplateHasSiteCode(t) ? t?.siteCode : null;
	return t?.name && siteCode ? `${t.name} (${siteCode})` : t?.name || siteCode || '';
};

// Can this card's template change, and to which? A non-virtual IR/Inception card, within its integration
// (the server's _updateTemplateID rule; an Inception card has no externalID, so that is never required).
export const cardTemplateChange = (card, templates = []) => {
	const current = templates.find((t) => t.credentialTemplateID === card?.externalCredentialTemplateID) || null;
	const eligible =
		card?.credentialTypeID === CREDENTIAL_TYPE_PASSCODE_CARD &&
		card.origin !== CREDENTIAL_ORIGIN.VIRTUAL &&
		Boolean(current) &&
		TEMPLATE_INTEGRATION_IDS.includes(current.integrationID);
	const options = eligible
		? templates.filter(
				(t) =>
					!t.deletedDate &&
					t.integrationID === current.integrationID &&
					t.credentialTemplateID !== card.externalCredentialTemplateID
			)
		: [];
	return { current, options, canChange: options.length > 0 };
};

// Toast for a mobile key from POST /credentials: issued (invited or not), reused, or invite failed.
export const mobileIssuedNotice = (result) => {
	const warnings = result?.warnings || [];
	if (warnings.some((w) => w.includes('invitation failed'))) {
		return { type: 'warning', title: 'Mobile key issued', text: warnings.join(' ') };
	}
	if (warnings.length) return { type: 'info', title: 'Mobile key already held', text: warnings.join(' ') };
	return {
		type: 'success',
		title: 'Mobile key issued',
		text: result?.invited ? 'The app invitation was sent.' : undefined
	};
};

// A lock delivery error without the server's "<vendor> <op> failed for/on lock N …:" prefix.
export const lockErrorMessage = (error) =>
	(error || '').replace(/^[\w() ]+? failed (for|on) lock \d+(?: \([^)]*\))?[^:]*:\s*/i, '');

// Toast for POST /credentials: an `existing` card was kept (retry), assigned, or moved from `previousHolderID`.
export const credentialCreatedNotice = (result, personID, previousHolderName) => {
	if (!result?.existing) return { type: 'success', title: 'Credential created successfully.' };
	const previousHolderID = result.previousHolderID ?? null;
	if (personID && previousHolderID && previousHolderID !== personID) {
		return {
			type: 'warning',
			title: 'Card moved',
			text: `This card already existed. It was moved from ${previousHolderName || 'its previous holder'} to this person.`
		};
	}
	if (personID && !previousHolderID) {
		return { type: 'success', title: 'Card assigned', text: 'This card already existed. It is now assigned.' };
	}
	return { type: 'info', title: 'Card already exists', text: 'Nothing was changed.' };
};
